import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type { FilterProperty, SelectOption } from '@/components/admin/SysadminSPA/shared/filterTypes';
import REFERENCE_MATERIAL_FILTER_PROPERTIES from './filterConfig';

interface RawProgram {
  id: number;
  full_title_with_edition: string;
}

// AIDEV-NOTE: Fetches programs and tags independently from the filtered data,
// so dropdown options don't change when filters are applied. Matches the legacy
// pattern where options are pre-populated from the server.
const useFilterOptions = (): FilterProperty[] => {
  const { data: programs = [] } = useQuery({
    queryFn: async (): Promise<RawProgram[]> => {
      const response = await Axios.get(
        Routes.plato_api_programs_path({ non_archived: true, only: ['id', 'full_title_with_edition'] }),
      );

      return response.data.data;
    },
    queryKey: [QueryKeys.ContentPrograms, 'filter-options'],
  });

  const { data: titles = [] } = useQuery({
    queryFn: async (): Promise<SelectOption[]> => {
      const response = await Axios.get(
        Routes.plato_api_reference_materials_path({ only: ['title'], page: 1, per_page: 10000 }),
      );

      const seen = new Set<string>();

      return (response.data.data ?? []).reduce((acc: SelectOption[], rm: { title: string }) => {
        if (rm.title && !seen.has(rm.title)) {
          seen.add(rm.title);
          acc.push({ label: rm.title, value: rm.title });
        }

        return acc;
      }, []);
    },
    queryKey: [QueryKeys.ContentReferenceMaterials, 'filter-titles'],
  });

  const { data: tags = [] } = useQuery({
    queryFn: async (): Promise<SelectOption[]> => {
      const response = await Axios.get(
        Routes.plato_api_tags_path({ name: ['Library', 'Diversity'], only: ['id', 'value'] }),
      );

      return (response.data.data ?? []).map((t: { value: string }) => ({
        label: t.value,
        value: t.value,
      }));
    },
    queryKey: [QueryKeys.ReferenceMaterialFilterTags],
  });

  return useMemo(() => {
    const programOptions: SelectOption[] = programs
      .filter(p => p.full_title_with_edition && !p.full_title_with_edition.includes('{placeholder}'))
      .map(p => ({ label: p.full_title_with_edition, value: String(p.id) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const tagOptions = tags.filter(t => t.label).sort((a, b) => a.label.localeCompare(b.label));
    const titleOptions = [...titles].sort((a, b) => a.label.localeCompare(b.label));

    const optionsMap: Record<string, SelectOption[]> = {
      programs_id: programOptions,
      reference_material_translations_title: titleOptions,
      tags_value_en: tagOptions,
    };

    return REFERENCE_MATERIAL_FILTER_PROPERTIES.map(prop => ({
      ...prop,
      options: optionsMap[prop.value],
    }));
  }, [programs, tags, titles]);
};

export default useFilterOptions;
